import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, MessageSquare, Plus } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/Button';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../components/StandardTable';
import AdminModal from '../components/AdminModal';
import AdminPageShell from '../components/AdminPageShell';
import { PRIORITY_CHOICES } from '../../citizen/lib/intakeChoices';
import { formatDate } from '../lib/format';

/**
 * Engagement Prompts — the human-curated micro-engagement bank
 * (CITIZEN_PROGRESSIVE_ONBOARDING_PLAN.md §5, decision §9-2). Prompts are
 * authored here as drafts and only reach citizens when a person publishes
 * them after the §6 neutrality checklist: topics never positions, no
 * party-fishing, balanced options, local-Colorado content only.
 */

const KINDS = [
  { value: 'direct_choice', label: 'Direct choice' },
  { value: 'this_or_that', label: 'This or that' },
  { value: 'reaction', label: 'Reaction' },
  { value: 'confirm_inference', label: 'Confirm inference (held until Tier 2)' },
] as const;

interface PromptOption {
  id: string;
  label: string;
  topic?: string;
}

interface EngagementPrompt {
  id: string;
  kind: string;
  topic_tags: string[];
  content: { prompt: string; options: PromptOption[] };
  status: 'draft' | 'published' | 'retired';
  created_at: string;
  updated_at: string;
  response_count: number;
  skip_count: number;
}

const inputClasses = 'input';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const COLUMN_COUNT = 6;

interface PromptForm {
  kind: string;
  prompt: string;
  options: Array<{ label: string; topic: string }>;
}

const emptyForm = (): PromptForm => ({
  kind: 'direct_choice',
  prompt: '',
  options: [
    { label: '', topic: '' },
    { label: '', topic: '' },
  ],
});

const formFromPrompt = (p: EngagementPrompt): PromptForm => ({
  kind: p.kind,
  prompt: p.content?.prompt ?? '',
  options: (p.content?.options ?? []).map((o) => ({ label: o.label, topic: o.topic ?? '' })),
});

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.errors?.[0]?.msg ?? fallback;

const EngagementPromptsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PromptForm>(emptyForm);

  const prompts = useQuery({
    queryKey: ['admin-engagement-prompts'],
    queryFn: async () => {
      const response = await api.get('/admin/engagement-prompts');
      return (response.data.data ?? []) as EngagementPrompt[];
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-engagement-prompts'] });

  const buildBody = () => ({
    kind: form.kind,
    // topic_tags derive from the options' topics — one taxonomy, no drift.
    topic_tags: [...new Set(form.options.map((o) => o.topic).filter(Boolean))],
    content: {
      prompt: form.prompt.trim(),
      options: form.options
        .filter((o) => o.label.trim())
        .map((o, i) => ({ id: `opt-${i + 1}`, label: o.label.trim(), ...(o.topic ? { topic: o.topic } : {}) })),
    },
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      editingId
        ? api.put(`/admin/engagement-prompts/${editingId}`, buildBody())
        : api.post('/admin/engagement-prompts', buildBody()),
    onSuccess: () => {
      toast.success(editingId ? 'Prompt updated' : 'Prompt saved as draft');
      setEditorOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save prompt')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.put(`/admin/engagement-prompts/${id}/status`, { status }),
    onSuccess: (_r, vars) => {
      toast.success(vars.status === 'published' ? 'Prompt published' : `Prompt ${vars.status}`);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update status')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/engagement-prompts/${id}`),
    onSuccess: () => {
      toast.success('Draft deleted');
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to delete prompt')),
  });

  const rows = (prompts.data ?? []).filter((p) => statusFilter === 'all' || p.status === statusFilter);

  const validOptions = form.options.filter((o) => o.label.trim());
  const saveDisabled =
    !form.prompt.trim() || validOptions.length < 2 || validOptions.some((o) => !o.topic);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setEditorOpen(true);
  };
  const openEdit = (p: EngagementPrompt) => {
    setEditingId(p.id);
    setForm(formFromPrompt(p));
    setEditorOpen(true);
  };

  const setOption = (index: number, patch: Partial<{ label: string; topic: string }>) =>
    setForm((cur) => ({
      ...cur,
      options: cur.options.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    }));

  return (
    <AdminPageShell
      title="Engagement Prompts"
      description="The human-curated micro-engagement bank served to citizens — one card, one tap, max two per day"
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            aria-label="Filter by status"
            className="input h-9 w-auto py-0"
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="retired">Retired</option>
          </select>
          <Button variant="primary" size="sm" icon={Plus} onClick={openCreate}>
            New Prompt
          </Button>
        </div>

        {prompts.isError ? (
          <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
            <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
            <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
              Couldn't load engagement prompts.
            </p>
            <Button variant="outline" size="sm" onClick={() => prompts.refetch()}>Retry</Button>
          </div>
        ) : (
          <StandardTable horizontalScroll loading={prompts.isFetching && !prompts.isLoading}>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Prompt</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="md">Kind</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="md">Topics</StandardTableHeaderCell>
                <StandardTableHeaderCell>Status</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="lg">Responses</StandardTableHeaderCell>
                <StandardTableHeaderCell>Actions</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {prompts.isLoading ? (
                <LoadingTableState colSpan={COLUMN_COUNT} />
              ) : rows.length === 0 ? (
                <EmptyTableState
                  colSpan={COLUMN_COUNT}
                  message="No prompts yet — write the first one and publish it after the neutrality checklist"
                  icon={<MessageSquare className="h-8 w-8" />}
                />
              ) : (
                rows.map((p) => (
                  <StandardTableRow key={p.id} onClick={() => openEdit(p)}>
                    <StandardTableCell>
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">
                        {p.content?.prompt || '—'}
                      </span>
                      <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        Updated {formatDate(p.updated_at)}
                      </span>
                    </StandardTableCell>
                    <StandardTableCell responsiveHide="md">
                      {KINDS.find((k) => k.value === p.kind)?.label ?? p.kind}
                    </StandardTableCell>
                    <StandardTableCell responsiveHide="md">{(p.topic_tags ?? []).join(', ') || '—'}</StandardTableCell>
                    <StandardTableCell>
                      <span
                        className={
                          p.status === 'published'
                            ? 'inline-flex rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-800 dark:bg-success-900 dark:bg-opacity-40 dark:text-success-300'
                            : p.status === 'retired'
                              ? 'inline-flex rounded-full bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-800 dark:bg-warning-900 dark:bg-opacity-40 dark:text-warning-300'
                              : 'inline-flex rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
                        }
                      >
                        {p.status}
                      </span>
                    </StandardTableCell>
                    <StandardTableCell responsiveHide="lg">
                      {p.response_count}
                      {p.skip_count > 0 && (
                        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary"> ({p.skip_count} skips)</span>
                      )}
                    </StandardTableCell>
                    <StandardTableCell>
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        {p.status !== 'published' && (
                          <Button variant="secondary" size="sm"
                            onClick={() => statusMutation.mutate({ id: p.id, status: 'published' })}>
                            Publish
                          </Button>
                        )}
                        {p.status === 'published' && (
                          <Button variant="outline" size="sm"
                            onClick={() => statusMutation.mutate({ id: p.id, status: 'retired' })}>
                            Retire
                          </Button>
                        )}
                        {p.status === 'draft' && (
                          <Button variant="outline" size="sm" onClick={() => deleteMutation.mutate(p.id)}>
                            Delete
                          </Button>
                        )}
                      </div>
                    </StandardTableCell>
                  </StandardTableRow>
                ))
              )}
            </StandardTableBody>
          </StandardTable>
        )}

        <AdminModal
          isOpen={editorOpen}
          title={editingId ? 'Edit Prompt' : 'New Prompt'}
          onClose={() => setEditorOpen(false)}
          footer={
            <>
              <Button variant="secondary" size="sm" onClick={() => setEditorOpen(false)}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={saveDisabled}
                loading={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
                {editingId ? 'Save Changes' : 'Save Draft'}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <div>
              <label htmlFor="prompt-kind" className={labelClasses}>Kind</label>
              <select id="prompt-kind" value={form.kind}
                onChange={(event) => setForm((cur) => ({ ...cur, kind: event.target.value }))}
                className={inputClasses}>
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="prompt-text" className={labelClasses}>Prompt *</label>
              <textarea id="prompt-text" rows={2} value={form.prompt}
                placeholder="Which of these matters more to you right now?"
                onChange={(event) => setForm((cur) => ({ ...cur, prompt: event.target.value }))}
                className={inputClasses} />
            </div>
            <div className="space-y-2">
              <span className={labelClasses}>Options * (2–5; each answer teaches one topic)</span>
              {form.options.map((option, index) => (
                <div key={index} className="grid grid-cols-[1fr_180px] gap-2">
                  <input type="text" value={option.label} placeholder={`Option ${index + 1}`}
                    aria-label={`Option ${index + 1} label`}
                    onChange={(event) => setOption(index, { label: event.target.value })}
                    className={inputClasses} />
                  <select value={option.topic} aria-label={`Option ${index + 1} topic`}
                    onChange={(event) => setOption(index, { topic: event.target.value })}
                    className={inputClasses}>
                    <option value="">Topic…</option>
                    {PRIORITY_CHOICES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              ))}
              <div className="flex gap-2">
                {form.options.length < 5 && (
                  <Button variant="outline" size="sm"
                    onClick={() => setForm((cur) => ({ ...cur, options: [...cur.options, { label: '', topic: '' }] }))}>
                    Add option
                  </Button>
                )}
                {form.options.length > 2 && (
                  <Button variant="outline" size="sm"
                    onClick={() => setForm((cur) => ({ ...cur, options: cur.options.slice(0, -1) }))}>
                    Remove last
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Publish checklist (plan §6): measures a topic, never a position · no party-fishing ·
              balanced options · local Colorado content only · would read as service on the
              &quot;what the app knows about me&quot; screen.
            </p>
          </div>
        </AdminModal>
      </div>
    </AdminPageShell>
  );
};

export default EngagementPromptsPage;
